import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Avatar, Button, Col, DatePicker, Input, InputNumber, Row, Select, Space, Switch, Tooltip } from 'antd'
import {
    CarOutlined,
    GiftOutlined,
    HomeOutlined,
    ReloadOutlined,
    SearchOutlined,
    ShoppingCartOutlined,
    ShoppingOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    UserOutlined,
} from '@ant-design/icons'
import { PUSH_SEGMENT_PLAN } from '../../users/users.controller'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { getPrice, getPriceShort } from '../../../helpers/price'
import dayjs from 'dayjs'
import {
    CardList,
    ListCard,
    ListChip,
    ListMedia,
    ListToolbar,
    ListToolbarBreak,
    ListToolbarChips,
    ListToolbarField,
} from '../../../components/card_list/CardList'
import controller from './carts.controller'
import {
    ABANDONED_HOURS,
    ACTIVE_MINUTES,
    activityState,
    deliveryLabel,
    formatDateTime,
    formatPhone,
    formatTime,
    timeAgo,
    userName,
} from './carts.helpers'
import './carts.less'

const debounce = (fn: (value: string) => void, ms: number) => {
    let timer: ReturnType<typeof setTimeout> | null = null

    return (value: string) => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => fn(value), ms)
    }
}

/** Точка рядом с датой: зелёная пульсирует — корзину меняли только что */
export const ActivityDot = ({ iso }: { iso: string | null }) => (
    <span className={`carts-dot carts-dot--${activityState(iso)}`} />
)

export const LiveSwitch = ({ live, updatedAt, onChange, onRefresh }: {
    live: boolean
    updatedAt: Date | null
    onChange: (value: boolean) => void
    onRefresh: () => void
}) => (
    <div className="carts-live">
        <Tooltip title={i.t('carts:live.hint')}>
            <label className="carts-live__switch">
                <Switch size="small" checked={live} onChange={onChange} />
                <span className={`carts-live__label${live ? ' carts-live__label--on' : ''}`}>
                    {live && <span className="carts-dot carts-dot--active" />}
                    {i.t('carts:live.label')}
                </span>
            </label>
        </Tooltip>
        {updatedAt && <span className="carts-muted">{i.t('carts:live.updated', { time: formatTime(updatedAt) })}</span>}
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={onRefresh}>{i.t('carts:live.refresh')}</Button>
    </div>
)

/** Шрифт плитки по длине значения: десятки миллионов в корзинах в 22px не помещаются */
const statValueSize = (value: React.ReactNode): string => {
    const length = typeof value === 'string' || typeof value === 'number' ? String(value).length : 0

    return length > 14 ? ' carts-stat__value--xs' : length > 10 ? ' carts-stat__value--sm' : ''
}

const StatTile = ({ value, label, hint, tone, active, onClick }: {
    value: React.ReactNode
    label: string
    hint?: React.ReactNode
    tone?: 'green' | 'orange'
    active?: boolean
    onClick?: () => void
}) => (
    <Col xs={12} md={8} xl={4}>
        <Tooltip title={hint}>
            <div
                className={`carts-stat${onClick ? ' carts-stat--clickable' : ''}${active ? ' carts-stat--active' : ''}${tone ? ` carts-stat--${tone}` : ''}`}
                onClick={onClick}
            >
                <div className={`carts-stat__value${statValueSize(value)}`}>{value}</div>
                <div className="carts-stat__label">{label}</div>
            </div>
        </Tooltip>
    </Col>
)

const ProductThumbs = ({ cart }: { cart: any }) => {
    const more = cart.positions - cart.preview.length

    return (
        <Space size={6}>
            <Avatar.Group maxCount={5} size={36} shape="square">
                {cart.preview.map((product: any) => (
                    <Tooltip key={product.productId} title={`${product.name ?? `#${product.productId}`} × ${product.count}`}>
                        <Avatar shape="square" size={36} src={product.image ?? undefined} icon={product.image ? undefined : <ShoppingOutlined />} className="carts-thumb" />
                    </Tooltip>
                ))}
            </Avatar.Group>
            {more > 0 && <span className="carts-muted">+{more}</span>}
        </Space>
    )
}

const ACTIVITY_FILTERS = ['all', 'now', 'today', 'abandoned']

const SORT_KEYS = ['activity_at', 'created_at', 'sum', 'positions']

const SORT_LABELS: Record<string, string> = {
    activity_at: 'carts:fields.activity',
    created_at: 'carts:fields.created',
    sum: 'carts:fields.sum',
    positions: 'carts:fields.positions',
}

const CartCard = ({ cart, flash, withWarehouses }: { cart: any, flash: boolean, withWarehouses: boolean }) => {
    const user = cart.user
    const contact = formatPhone(user?.phone) || user?.email

    return (
        // Обёртка — для вспышки карточки, которая изменилась с прошлого live-обновления
        <div className={`carts-card${flash ? ' carts-card--flash' : ''}`}>
            <ListCard
                href={`/carts/${cart.id}`}
                muted={!!user?.deleted}
                media={<ListMedia icon={<UserOutlined />} tone={user?.registered ? undefined : 'gray'} />}
                title={userName(user)}
                titleHint={userName(user)}
                titleExtra={<>
                    {user?.name && !user.registered && <ListChip>{i.t('carts:user.guest')}</ListChip>}
                    {user?.deleted && <ListChip tone="danger">{i.t('carts:user.deleted')}</ListChip>}
                </>}
                subtitle={<>
                    {contact && <>{contact} · </>}
                    {i.t('carts:cart')} #{cart.id}
                </>}
                text={<ProductThumbs cart={cart} />}
                chips={(cart.promoCode || cart.delivery || (withWarehouses && cart.warehouse)) && <>
                    {cart.promoCode && (cart.promoCode.code
                        ? <ListChip tone="primary" icon={<GiftOutlined />} tooltip={i.t('carts:fields.promo')}>{cart.promoCode.code}</ListChip>
                        : <ListChip icon={<GiftOutlined />}>{i.t('carts:detail.invitedShort')}</ListChip>
                    )}
                    {cart.delivery &&
                        <ListChip icon={cart.delivery === 'pickup' ? <HomeOutlined /> : <CarOutlined />} tooltip={i.t('carts:fields.delivery')}>
                            {deliveryLabel(cart.delivery)}
                        </ListChip>
                    }
                    {withWarehouses && cart.warehouse &&
                        <ListChip tooltip={i.t('carts:filters.warehouseHint')}>{cart.warehouse.name ?? `#${cart.warehouse.id}`}</ListChip>
                    }
                </>}
                // «16 мин назад» с точкой активности и сумма с копейками — без обрезки
                metricWidth={136}
                metrics={[
                    {
                        value: cart.positions,
                        label: `${i.t('carts:fields.positions')} · ${cart.quantity} ${i.t('carts:units.pcs')}`,
                    },
                    { value: getPrice(cart.sum), label: i.t('carts:fields.sum'), hint: i.t('carts:stats.sumHint') },
                    {
                        value: <><ActivityDot iso={cart.activityAt} />{timeAgo(cart.activityAt)}</>,
                        label: i.t('carts:fields.activity'),
                        hint: formatDateTime(cart.activityAt),
                    },
                    { value: timeAgo(cart.createdAt), label: i.t('carts:fields.created'), hint: formatDateTime(cart.createdAt) },
                ]}
            />
        </div>
    )
}

const CartsToolbar = observer(() => {

    const searchProducts = useMemo(() => debounce((value: string) => controller.searchProducts(value), 300), [])

    const filters = controller.filters
    const warehouses = controller.warehouses
    const priceTypes = controller.priceTypes

    const productOptions = controller.productOption && !controller.productOptions.some(o => o.value === controller.productOption?.value)
        ? [controller.productOption, ...controller.productOptions]
        : controller.productOptions

    return (
        <ListToolbar>
            <ListToolbarChips>
                {ACTIVITY_FILTERS.map(key => {
                    const active = (filters.activity ?? 'all') === key

                    return (
                        <ListChip key={key} active={active} onClick={() => controller.setFilter('activity', key === 'all' ? null : key)}>
                            {i.t(`carts:activity.${key}`)}
                        </ListChip>
                    )
                })}
            </ListToolbarChips>

            <ListToolbarField wide>
                <Input.Search
                    allowClear
                    placeholder={i.t('carts:filters.search')}
                    defaultValue={filters.search ?? ''}
                    onSearch={(value) => controller.setFilter('search', value.trim() === '' ? null : value.trim())}
                />
            </ListToolbarField>

            <ListToolbarBreak
                title={i.t('carts:filters.title')}
                open={controller.filtersOpen}
                onToggle={() => controller.toggleFilters()}
                count={controller.panelFiltersCount}
                extra={controller.hasFilters &&
                    <Button size="small" type="link" onClick={() => controller.resetFilters()}>{i.t('carts:filters.reset')}</Button>
                }
            />

            {controller.filtersOpen && <>
                <ListToolbarField>
                    <Select
                        allowClear
                        showSearch
                        style={{ width: '100%' }}
                        placeholder={i.t('carts:filters.product')}
                        filterOption={false}
                        value={filters.product_id ?? undefined}
                        options={productOptions}
                        onSearch={searchProducts}
                        onChange={(value: any, option: any) => controller.filterByProduct(value ?? null, option?.label ?? '')}
                        notFoundContent={null}
                    />
                </ListToolbarField>
                <ListToolbarField>
                    <Space.Compact block>
                        <InputNumber
                            min={0}
                            style={{ width: '50%' }}
                            placeholder={i.t('carts:filters.sumFrom')}
                            value={filters.sum_from}
                            onChange={(value) => controller.setFilter('sum_from', value)}
                        />
                        <InputNumber
                            min={0}
                            style={{ width: '50%' }}
                            placeholder={i.t('carts:filters.sumTo')}
                            value={filters.sum_to}
                            onChange={(value) => controller.setFilter('sum_to', value)}
                        />
                    </Space.Compact>
                </ListToolbarField>
                <ListToolbarField>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('carts:filters.users')}
                        value={filters.registered ?? undefined}
                        onChange={(value) => controller.setFilter('registered', value)}
                        options={[
                            { label: i.t('carts:filters.registered'), value: 1 },
                            { label: i.t('carts:filters.guests'), value: 0 },
                        ]}
                    />
                </ListToolbarField>
                <ListToolbarField>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('carts:filters.promo')}
                        value={filters.has_promo_code ?? undefined}
                        onChange={(value) => controller.setFilter('has_promo_code', value)}
                        options={[
                            { label: i.t('carts:filters.withPromo'), value: 1 },
                            { label: i.t('carts:filters.withoutPromo'), value: 0 },
                        ]}
                    />
                </ListToolbarField>
                {warehouses.length > 0 && (
                    <ListToolbarField>
                        <Tooltip title={i.t('carts:filters.warehouseHint')}>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                placeholder={i.t('carts:filters.warehouse')}
                                value={filters.warehouse_id ?? undefined}
                                onChange={(value) => controller.setFilter('warehouse_id', value)}
                                options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name ?? `#${warehouse.id}` }))}
                            />
                        </Tooltip>
                    </ListToolbarField>
                )}
                {priceTypes.length > 0 && (
                    <ListToolbarField>
                        <Tooltip title={i.t('carts:filters.priceTypesHint')}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                style={{ width: '100%' }}
                                placeholder={i.t('carts:filters.priceTypes')}
                                value={filters.price_types}
                                onChange={(ids: number[]) => controller.setFilter('price_types', ids ?? [])}
                                options={priceTypes.map(priceType => ({ value: priceType.id, label: priceType.label }))}
                            />
                        </Tooltip>
                    </ListToolbarField>
                )}
                <ListToolbarField>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('carts:fields.delivery')}
                        value={filters.delivery ?? undefined}
                        onChange={(value) => controller.setFilter('delivery', value)}
                        options={[
                            { label: i.t('carts:delivery.delivery'), value: 'delivery' },
                            { label: i.t('carts:delivery.pickup'), value: 'pickup' },
                        ]}
                    />
                </ListToolbarField>
                <ListToolbarField>
                    <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        format="DD.MM.YYYY"
                        placeholder={[i.t('carts:filters.activityFrom'), i.t('carts:filters.activityTo')]}
                        // Управляемое: панель сворачивается, а выбранные даты должны остаться
                        value={filters.activity_from || filters.activity_to
                            ? [filters.activity_from ? dayjs.unix(filters.activity_from) : null, filters.activity_to ? dayjs.unix(filters.activity_to) : null]
                            : null
                        }
                        allowEmpty={[true, true]}
                        onChange={(dates: any) => controller.setFilters({
                            activity_from: dates?.[0] ? dates[0].startOf('day').unix() : null,
                            activity_to: dates?.[1] ? dates[1].endOf('day').unix() : null,
                        })}
                    />
                </ListToolbarField>
            </>}
        </ListToolbar>
    )
})

// Поле сортировки и направление — вместо заголовков колонок
const CartsSort = observer(() => {
    const desc = controller.sorting.action === 'desc'

    return (
        <Space.Compact>
            <Select
                size="small"
                popupMatchSelectWidth={false}
                value={controller.sorting.type}
                options={SORT_KEYS.map(key => ({ value: key, label: i.t(SORT_LABELS[key]) }))}
                onChange={(type: string) => controller.setSort(type, controller.sorting.action)}
            />
            <Tooltip title={i.t(desc ? 'carts:sort.desc' : 'carts:sort.asc')}>
                <Button
                    size="small"
                    aria-label={i.t(desc ? 'carts:sort.desc' : 'carts:sort.asc')}
                    icon={desc ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                    onClick={() => controller.setSort(controller.sorting.type, desc ? 'asc' : 'desc')}
                />
            </Tooltip>
        </Space.Compact>
    )
})

export const Carts = observer(() => {

    const history = useHistory()

    useEffect(() => {
        controller.refresh()
        controller.loadPriceTypes()
        controller.startLive()

        // Вернулись на вкладку — сразу догоняем, не дожидаясь следующего тика
        const onVisible = () => {
            if (!document.hidden && controller.live) controller.refresh(true)
        }
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            controller.stopLive()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    const stats = controller.stats
    const filters = controller.filters
    const withWarehouses = controller.warehouses.length > 0

    const toggleActivity = (value: string) => controller.setFilter('activity', filters.activity === value ? null : value)

    return (
        <>
            <Titles
                title={i.t('carts:title')}
                breadcrumb={[{ title: i.t('carts:title') }]}
                buttons={controller.pagination.total > 0 ? [
                    { title: i.t('carts:export.button'), click: () => history.push(controller.exportUrl) },
                    { title: i.t('carts:push.button'), type: 'primary', plan: PUSH_SEGMENT_PLAN, click: () => history.push(controller.pushUrl) },
                ] : []}
            />

            <LiveSwitch
                live={controller.live}
                updatedAt={controller.updatedAt}
                onChange={(value) => controller.setLive(value)}
                onRefresh={() => controller.refresh()}
            />

            {stats && (
                <Row gutter={[12, 12]} className="carts-stats">
                    <StatTile value={stats.total} label={i.t('carts:stats.total')} />
                    {/* Суммы — целыми, копейки в сводке не нужны; точное значение в подсказке */}
                    <StatTile
                        value={getPriceShort(stats.sum)}
                        label={i.t('carts:stats.sum')}
                        hint={<>{getPrice(stats.sum)}<br />{i.t('carts:stats.sumHint')}</>}
                    />
                    <StatTile
                        value={getPriceShort(stats.avgSum)}
                        label={i.t('carts:stats.avgSum')}
                        hint={<>{getPrice(stats.avgSum)}<br />{i.t('carts:stats.sumHint')}</>}
                    />
                    <StatTile
                        value={<><span className={`carts-dot carts-dot--${stats.activeNow > 0 ? 'active' : 'unknown'}`} />{stats.activeNow}</>}
                        label={i.t('carts:stats.activeNow')}
                        hint={i.t('carts:stats.activeNowHint', { n: ACTIVE_MINUTES })}
                        tone="green"
                        active={filters.activity === 'now'}
                        onClick={() => toggleActivity('now')}
                    />
                    <StatTile
                        value={stats.activeToday}
                        label={i.t('carts:stats.activeToday')}
                        active={filters.activity === 'today'}
                        onClick={() => toggleActivity('today')}
                    />
                    <StatTile
                        value={stats.abandoned}
                        label={i.t('carts:stats.abandoned')}
                        hint={i.t('carts:stats.abandonedHint', { n: ABANDONED_HOURS })}
                        tone="orange"
                        active={filters.activity === 'abandoned'}
                        onClick={() => toggleActivity('abandoned')}
                    />
                </Row>
            )}

            {stats && stats.topProducts.length > 0 && (
                <div className="carts-top">
                    <div className="carts-top__title">{i.t('carts:top.title')}</div>
                    <div className="carts-top__list">
                        {stats.topProducts.map((product: any) => {
                            const active = filters.product_id === product.productId

                            return (
                                <Tooltip key={product.productId} title={i.t('carts:top.hint')}>
                                    <div
                                        className={`carts-top__item${active ? ' carts-top__item--active' : ''}`}
                                        onClick={() => controller.filterByProduct(active ? null : product.productId, product.name)}
                                    >
                                        <Avatar shape="square" size={40} src={product.image ?? undefined} icon={product.image ? undefined : <ShoppingOutlined />} className="carts-thumb" />
                                        <div className="carts-top__meta">
                                            <div className="carts-top__name">{product.name}</div>
                                            <div className="carts-muted">{i.t('carts:top.carts', { n: product.carts })} · {product.quantity} {i.t('carts:units.pcs')}</div>
                                        </div>
                                    </div>
                                </Tooltip>
                            )
                        })}
                    </div>
                </div>
            )}

            <CardList
                items={controller.list}
                count={controller.pagination.total}
                loading={controller.loading && !controller.loaded}
                toolbar={<CartsToolbar />}
                extra={controller.list.length > 0 && <CartsSort />}
                renderItem={(cart: any) => (
                    <CartCard cart={cart} flash={controller.flashIds.includes(cart.id)} withWarehouses={withWarehouses} />
                )}
                empty={{
                    icon: controller.hasFilters ? <SearchOutlined /> : <ShoppingCartOutlined />,
                    title: i.t('carts:empty'),
                    action: controller.hasFilters
                        ? <Button onClick={() => controller.resetFilters()}>{i.t('carts:filters.reset')}</Button>
                        : undefined,
                }}
                pagination={{
                    current: controller.pagination.page,
                    pageSize: controller.pagination.limit,
                    total: controller.pagination.total,
                    onChange: (page) => {
                        controller.setPage(page)
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                    },
                }}
            />
        </>
    )
})
